import Btn from 'src/components-v2/Inputs/Button/Btn';
import Box from 'src/components-v2/Layout/Box/Box';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import Wrapper from 'src/containers/Wrapper/Wrapper';
import type { TestimonialSectionShape } from 'src/types/contentful';
import styled from 'threads5/styles/styled';

export const StyledFullWidth = styled(Wrapper, {
  shouldForwardProp: (prop) => {
    return !['background'].includes(prop.toString());
  },
})<{ background?: string }>(({ theme, background }) => {
  return {
    background:
      background ??
      theme.palette['web3/component/testimonial-section/background'],
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('xs')]: {
      padding: '32px 0',
    },
    [theme.breakpoints.up('md')]: {
      padding: '120px 0',
    },
  };
});

export const StyledFullWidthBackgroundImage = styled(CmsMediaSwitch)(
  ({ theme }) => {
    return {
      position: 'absolute',
      zIndex: 0,
      [theme.breakpoints.up('xs')]: {
        height: 'auto',
        width: '100%',
        maxHeight: '70%',
        maxWidth: 'none',
        objectFit: 'cover',
      },
      [theme.breakpoints.up('sm')]: {
        height: '100%',
        width: 'auto',
        maxWidth: '70%',
        maxHeight: 'none',
      },
    };
  },
);

export const StyledCard = styled(Box, {
  shouldForwardProp: (prop) => {
    return !['background', 'hasCta'].includes(prop.toString());
  },
})<{
  background?: string;
  hasCta?: boolean;
}>(({ theme, background, hasCta }) => {
  return {
    background:
      background ??
      theme.palette['web3/component/testimonial-section/background'],
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
      padding: '28px',
    },
    [theme.breakpoints.up('sm')]: {
      borderRadius: '24px',
      padding: '32px',

      // Expanded CTA region is only visible on desktop/tablet
      ...(hasCta && {
        ':hover, :focus-within': {
          '.testimonial-section-card-background-image-wrapper': {
            scale: 1.04,
          },
        },
      }),
    },
    [theme.breakpoints.up('md')]: {
      padding: '68px',
    },
  };
});

export const StyledCardCtaRegion = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    zIndex: 2,
    inset: 0,
    cursor: 'pointer',

    // Only expand CTA region on desktop/tablet
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  };
});

export const StyledCardBackgroundImageWrapper = styled('div')({
  position: 'absolute',
  zIndex: 0,
  inset: 0,
  transition: 'scale .4s ease',
});

export const StyledCardBackgroundImage = styled(CmsMediaSwitch)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const StyledQuote = styled(Stack, {
  shouldForwardProp: (prop) => {
    return !['appearance'].includes(prop.toString());
  },
})<{
  appearance: TestimonialSectionShape['appearance'];
}>(({ theme, appearance }) => {
  return {
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.up('xs')]: {
      gap: theme.spacing(3),
    },
    [theme.breakpoints.up('sm')]: {
      paddingRight: appearance === 'Full Width' ? '107px' : '67px',
    },
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(4),
      paddingRight: appearance === 'Full Width' ? '62px' : 0,
    },
    // Company logo styles
    img: {
      [theme.breakpoints.up('xs')]: { maxWidth: '146px' },
      [theme.breakpoints.up('md')]: { maxWidth: '200px' },
    },
  };
});

export const StyledCtaWrapper = styled('div')(({ theme }) => {
  return {
    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '142px',
    },
  };
});

export const StyledCta = styled(Btn)({
  width: 'inherit',
  zIndex: 2,
});
